
export default class WDElement extends HTMLElement {
    
  //some shortcuts
  query = this.querySelector;
  queryAll = this.querySelectorAll;

  //maybe these?
  q = this.querySelector;
  qa = this.querySelectorAll;

  #listeners = new Map();
  #listenerId = 0;
  #abortController = null;
  #debugLogging = null;

  constructor(cfg)
  {
    super(cfg);
    
    // Create AbortController for managing all event listeners
    this.#abortController = new AbortController();
  }

  connectedCallback()
  {
  }

  disconnectedCallback()
  {
    // Defer cleanup to detect element moves vs actual removal
    // If element reconnects, connectedCallback will fire before timeout
    setTimeout(() => {
      if (!this.isConnected) {
        this.cleanupCallback();
      }
    }, 0);

  };

  /**
   * Cleanup callback called when element is truly removed from DOM (not just moved)
   * Override this in child classes to add custom cleanup logic
   */
  cleanupCallback()
  {
    //console.log('Cleanup callback',this.tagName);
    //this.replaceChildren();

    // Properly remove all children to trigger their disconnectedCallback
    // Note: replaceChildren() doesn't trigger disconnectedCallback on children
    while (this.firstChild)
    {
      this.firstChild.remove();
    }

    // Abort all event listeners via AbortController
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    
    // Clear listener tracking map
    this.#listeners.clear();
  }

  /**
   * Override replaceChildren() to properly trigger disconnectedCallback on removed children
   * Native replaceChildren() doesn't fire disconnectedCallback, causing memory leaks
   * @param {...Node} nodes - New child nodes to add after clearing
   */
  replaceChildren(...nodes)
  {
    // Remove all children using proper pattern that triggers disconnectedCallback
    while (this.firstChild) {
      this.firstChild.remove();
    }
    
    // Add new nodes if provided
    if (nodes.length > 0) {
      this.append(...nodes);
    }
  }

  empty(elem)
  {
    this.replaceChildren(elem);
  };

  getTag() { return this.tagName.toLowerCase(); };

  /**
   * Get the AbortController signal for use with DOM event listeners
   */
  get abortSignal() { return this.#abortController?.signal; };


  text(str)
  {
    if (str === undefined) return this.innerText;
    else 
    {
      this.innerText = str;
      return this;
    }
  };

  html(str)
  {
    if (str === undefined) return this.innerHTML;
    else 
    {
      this.innerHTML = str;
      return this;
    }
  };

  attr(key,val)
  {
    if (val === undefined) return this.getAttribute(key);
    else
    {
      this.setAttribute(key,val);
      return this;
    }
  };

  hide() { this.style.display = 'none'; };
  show() { this.style.display = ''; };

  /**
    * event and listener handling
    */
  on(evtNames,callback,options)
  {
    const evts = Array.isArray(evtNames) ? evtNames : evtNames.split(' ');
    const evtIds = [];
    
    evts.forEach( evt => {

      //"press" event, use touch instead of click
      if (evt == 'press')
      {
        evt = ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 'touchstart' : 'click';
      }

      try {
        const evtId = this.addListener({type:evt,callback:callback,options:options});
        evtIds.push(evtId);
      }
      catch (err)
      {
        console.log('Error adding event listener',err);
      }
      
    });

    return Array.isArray(evtNames) ? evtIds : evtIds[0];
  };
  
  
  off(evtNames,callback,options)
  {
    //try to find the key and remove it that way
    const evts = Array.isArray(evtNames) ? evtNames : evtNames.split(' ');
    
    evts.forEach( evt => {
    
      const listenerKeys = this.getListenerKeys({type:evt,callback:callback,options:options});

      if (listenerKeys.length > 0)
      {
        listenerKeys.forEach( key => this.offByKey(key) );
      }
      else
      {
        this.removeEventListener(evt,callback,options);
      }
    
    });

  };

  offByKey(evtKeys)
  {
    const keys = Array.isArray(evtKeys) ? evtKeys : [evtKeys];
    
    keys.forEach( key => {

      if ( this.#listeners.has(key) )
      {
        const l = this.#listeners.get(key);

        this.removeEventListener(l.type,l.callback,l.options);
        this.#listeners.delete(key);
      }
    });
  };

  offAll()
  {
    this.#listeners.clear();
    
    // Then abort the controller and create a new one for future listeners
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
  };

  once(evtNames,callback)
  {
    //try to find the key and remove it that way
    const evts = Array.isArray(evtNames) ? evtNames : evtNames.split(' ');
    const evtIds = [];
    
    evts.forEach( evt => {

      //get the listener key first, so we can remove this listener from the key once its fired
      const params = {
        type:evt, 
        options:{once:true},
        key: this.nextListenerKey(),
      };

      //our callback wrapper to handle listener cleanup
      params.callback = (event) => {
        callback(event);
        this.#listeners.delete(params.key);
      };
      
      const evtId = this.addListener(params);
      evtIds.push(evtId);
    });

    return Array.isArray(evtNames) ? evtIds : evtIds[0];
  };

  one(evtNames,callback)
  {
    return this.once(evtNames,callback);
  };

  nextListenerKey()
  {
    this.#listenerId++;
    return `e${this.#listenerId}`;
  };

  getListenerKeys(params)
  {
    const listenerKeys = [];

    //just passed an event type, so we're removing everything with that type
    if (params?.callback === undefined)
    {
      for (const [key,value] of this.#listeners)
      {
        if (params?.type == value?.type) listenerKeys.push(key);
      }
    }
    else
    {
      for ( const [key,value] of this.#listeners )
      {
        if (params?.type == value?.type && params?.callback === value?.callback && params?.options == value?.options)
        {
          listenerKeys.push(key);
          break;
        }
      }
    }

    return listenerKeys;
  }

  addListener(params)
  {
    try { 	   
      // Merge user options with AbortController signal
      const options = params.options || {};
      
      // If AbortController exists and user didn't provide their own signal, use ours
      if (this.#abortController && !options.signal) {
        options.signal = this.#abortController.signal;
      }
    
      this.addEventListener(params.type, params.callback, options);
      
      const key = params?.key || this.nextListenerKey();
      
      // Store the listener info (including merged options)
      const listenerData = {
        type: params.type,
        callback: params.callback,
        options: options
      };
      
      this.#listeners.set(key, listenerData);
      return key;
    }
    catch (err)
    {
      console.log('Error adding event listener',err);
    }
  };
  
  trigger(evtNames,data,bubbles = false)
  {
    const evts = Array.isArray(evtNames) ? evtNames : evtNames.split(' ');

    evts.forEach( evt => {

      const options = {};
      if (data) options.detail = data;
      if (bubbles) options.bubbles = bubbles;
    
      const event = new CustomEvent(evt,options);

      this.dispatchEvent(event);
    });
    
    return this;
  };

  set debugLogging(val)
  {
    this.#debugLogging = val;
  }

  debugLog(...data)
  {
    if (this.#debugLogging)
    {
      console.error(this.#debugLogging, ...data);
    }
  }

  bubbleEvents(elem,eventNames)
  {
    eventNames.forEach(eventName =>
    {
      elem.on(eventName, (evt) => this.trigger(eventName, evt?.detail || evt));
    });
  }
};

customElements.define('wd-element',WDElement);
